/**
 * Teacher → Activity  (Phase 5)
 *
 * Read-only audit-log feed of AI Copilot calls the teacher has made.
 * Shows kind (plan/questions/quiz), model, approval status (was Claude's
 * draft accepted into a saved lesson plan?), and timestamps.
 *
 * Powered by GET /api/school/portal/teacher/activity which reads
 * `lesson_plan_ai_logs` filtered to the current teacher.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  History, Sparkles, MessageCircleQuestion, ListChecks, CheckCircle2,
  Circle, Loader2,
} from 'lucide-react';
import { portalApi } from './teacherApi';

const KIND_META = {
  plan:      { Icon: Sparkles,             label: 'Plan draft',  color: 'text-indigo-600 bg-indigo-50' },
  suggest:   { Icon: Sparkles,             label: 'Plan draft',  color: 'text-indigo-600 bg-indigo-50' },
  questions: { Icon: MessageCircleQuestion, label: 'Questions',  color: 'text-sky-600 bg-sky-50' },
  quiz:      { Icon: ListChecks,           label: 'Quiz items',  color: 'text-violet-600 bg-violet-50' },
};

const fmtDateTime = (s) => s ? new Date(s).toLocaleString() : '';

export const TeacherActivity = () => {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['teacher-activity'],
    queryFn: () => portalApi('/teacher/activity'),
  });

  const approvedCount = rows.filter(r => r.approved).length;
  const pendingCount = rows.length - approvedCount;

  return (
    <div className="space-y-4" data-testid="teacher-activity-page">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <History className="h-5 w-5 text-indigo-500" /> Activity
        </h2>
        <p className="text-xs text-slate-500">
          Audit trail of every AI Copilot call you&apos;ve made and whether the suggestion
          was accepted into a saved lesson plan.
        </p>
      </div>

      {!isLoading && rows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="activity-kpi">
          <Card><CardContent className="pt-4">
            <div className="text-xs text-slate-500">Total AI calls</div>
            <div className="text-2xl font-semibold">{rows.length}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="text-xs text-slate-500">Accepted</div>
            <div className="text-2xl font-semibold text-emerald-600">{approvedCount}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="text-xs text-slate-500">Drafts not saved</div>
            <div className="text-2xl font-semibold text-amber-600">{pendingCount}</div>
          </CardContent></Card>
        </div>
      )}

      {isLoading ? (
        <div className="text-center text-slate-500 py-10">
          <Loader2 className="h-6 w-6 animate-spin inline-block" />
        </div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-500">
          No AI activity yet. Open <b>Lesson Plans</b> and click <b>Draft plan</b> to
          start using the Copilot.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {rows.map(r => {
            const meta = KIND_META[r.kind] || { Icon: History, label: r.kind, color: 'text-slate-600 bg-slate-50' };
            const Icon = meta.Icon;
            return (
              <Card key={r.id} data-testid={`activity-row-${r.id}`}>
                <CardContent className="py-3 flex items-center gap-3">
                  <div className={`rounded-md p-2 ${meta.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{meta.label}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {r.topic ? `“${r.topic}”` : '—'} · {r.model || 'claude'}
                      {(r.tokens_in || r.tokens_out) && (
                        <> · {r.tokens_in || 0}/{r.tokens_out || 0} tok</>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 hidden sm:block">
                    {fmtDateTime(r.created_at)}
                  </div>
                  {r.approved ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"
                           data-testid={`activity-status-approved-${r.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Saved
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500"
                           data-testid={`activity-status-pending-${r.id}`}>
                      <Circle className="h-3 w-3 mr-1" /> Draft only
                    </Badge>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeacherActivity;
